import React, { useEffect, useState } from 'react';
import {
  Spinner,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import {
  CheckmarkCircle16Filled,
  ErrorCircle16Filled,
  Circle16Filled,
} from '@fluentui/react-icons';
import type { CustomerFormSaveStatus } from './types';

const useStyles = makeStyles({
  saveStatus: {
    display: 'inline-flex',
    alignItems: 'center',
    ...shorthands.gap('6px'),
    fontSize: '12px',
    fontWeight: 400,
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.padding('2px', '8px'),
    ...shorthands.borderRadius('999px'),
    opacity: 1,
    transitionProperty: 'opacity',
    transitionDuration: '400ms',
    transitionTimingFunction: 'ease-out',
  },
  saveStatusFading: {
    opacity: 0,
  },
  saveStatusSaved: {
    color: tokens.colorPaletteGreenForeground1,
  },
  saveStatusError: {
    color: tokens.colorPaletteRedForeground1,
  },
  saveStatusUnsaved: {
    color: tokens.colorNeutralForeground2,
  },
});

interface SaveStatusBadgeProps {
  status: CustomerFormSaveStatus;
}

/**
 * Save-status pill: shows "Saving…" / "Saved" / "Unsaved changes" /
 * "Save failed" while autosave is in flight. Used on the customer
 * drawer header and the customer profile breadcrumb so the same form
 * surfaces present consistent feedback.
 *
 * The 'saved' state lingers ~400ms after the upstream status returns
 * to 'idle' so it fades out smoothly instead of disappearing
 * mid-glance.
 */
const SaveStatusBadge: React.FC<SaveStatusBadgeProps> = ({ status }) => {
  const styles = useStyles();
  const [displayStatus, setDisplayStatus] = useState(status);
  const [fading, setFading] = useState(false);

  useEffect(() => {
    if (status === 'idle' && displayStatus === 'saved') {
      setFading(true);
      const t = setTimeout(() => {
        setDisplayStatus('idle');
        setFading(false);
      }, 400);
      return () => clearTimeout(t);
    }
    setDisplayStatus(status);
    setFading(false);
  }, [status, displayStatus]);

  if (displayStatus === 'idle') return null;

  const fadeClass = fading ? ` ${styles.saveStatusFading}` : '';

  if (displayStatus === 'saving') {
    return (
      <span className={`${styles.saveStatus}${fadeClass}`}>
        <Spinner size="tiny" />
        Saving...
      </span>
    );
  }
  if (displayStatus === 'saved') {
    return (
      <span className={`${styles.saveStatus} ${styles.saveStatusSaved}${fadeClass}`}>
        <CheckmarkCircle16Filled />
        Saved
      </span>
    );
  }
  if (displayStatus === 'unsaved') {
    return (
      <span className={`${styles.saveStatus} ${styles.saveStatusUnsaved}${fadeClass}`}>
        <Circle16Filled />
        Unsaved changes
      </span>
    );
  }
  if (displayStatus === 'error') {
    return (
      <span className={`${styles.saveStatus} ${styles.saveStatusError}${fadeClass}`}>
        <ErrorCircle16Filled />
        Save failed
      </span>
    );
  }
  return null;
};

export default SaveStatusBadge;
